import { HttpClient } from '@angular/common/http';
import {
  Component,
  OnInit,
  AfterViewInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { NgxSuneditorComponent } from 'ngx-suneditor';
import { Observable, Observer, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService, LocalStorageService } from 'src/app/core/services';
import { CkEditorConfigService } from 'src/app/core/services/ck-editor-config.service';
import { environment } from 'src/environments/environment';

export interface JobAlert {
  _id?: string;
  title: string;
  jobCategory: any;
  jobType: string;
  state: string;
  totalVacancy?: number;
  salary?: string;
  qualification?: string[];
  ageLimit?: {
    min?: number;
    max?: number;
  };
  applyStartDate?: Date | string;
  applyEndDate?: Date | string;
  applicationFee?: {
    general?: string;
    obc?: string;
    sc?: string;
    st?: string;
    female?: string;
    ph?: string;
    ews?: string;
  };
  importantDates?: {
    title: string;
    value: string;
  }[];
  officialNotificationUrl?: string;
  applyLink?: string;
  importantLinks?: {
    title: string;
    value: string;
  }[];
  description?: string;
  image?: any;
  examStages?: any[];
  status?: boolean;
  publishedStatus?: string;
  createdAt?: string;
  updatedAt?: string;
}

@Component({
  selector: 'app-add-update-job-alert',
  templateUrl: './add-update-job-alert.component.html',
  styleUrls: ['./add-update-job-alert.component.scss'],
})
export class AddUpdateJobAlertComponent implements OnInit {
  @ViewChildren(NgxSuneditorComponent)
  editors!: QueryList<NgxSuneditorComponent>;

  returnIndex = 0;

  addUpdateJobAlertForm: FormGroup;
  idForUpdate: string;
  buttonLoading = false;
  imageLoading = false;
  thumbImageLoading = false;

  mediaBaseUrl = environment.mediaBaseUrl;
  mediaUploadUrl = environment.apiBaseUrl + '/api/media';

  /* ── Category dropdown ── */
  listOfCategories: any[] = [];
  totalCategoryCount = 0;
  categoryPageIndex = 1;
  categoryPageSize = 10;
  categorySearch = '';
  searchCategory: Subject<string> = new Subject<string>();

  /* ── State dropdown ── */
  listOfStates: any[] = [];
  statesLoading = false;

  /* ── Image meta (populated object from API) ── */
  imageMeta: { _id: string; path: string } | null = null;
  thumbMeta: { _id: string; path: string } | null = null;

  readonly ALL_INDIA = 'All India';

  attrEditorReady = false;

  public editorOptions: any;

  /* ── Static option lists ── */
  jobTypes = [
    'Central Govt',
    'State Govt',
    'Bank',
    'Railway',
    'Defence',
    'Teaching',
    'PSC',
    'Other',
  ];

  stageNames = [
    'Prelims',
    'Mains',
    'Interview',
    'Skill Test',
    'Document Verification',
  ];

  attributeTypes = ['text', 'textarea', 'link', 'number'];

  /* ── Suggested attribute names (autocomplete; user can still type custom) ── */
  suggestedStageAttributes: string[] = [];

  suggestedSubjectAttributes: string[] = [];

  feeCategories = [
    { label: 'General', control: 'feeGeneral' },
    { label: 'OBC', control: 'feeObc' },
    { label: 'SC', control: 'feeSc' },
    { label: 'ST', control: 'feeSt' },
    { label: 'Female', control: 'feeFemale' },
    { label: 'PH', control: 'feePh' },
    { label: 'EWS', control: 'feeEws' },
  ];

  constructor(
    private fb: FormBuilder,
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private msg: NzMessageService,
    private ckEditorService: CkEditorConfigService,
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService,
  ) {
    this.idForUpdate = this.activatedRoute.snapshot.params['id'];
    this.returnIndex =
      Number(this.activatedRoute.snapshot.queryParams['index']) || 0;

    this.addUpdateJobAlertForm = this.fb.group({
      title: [null, [Validators.required]],
      jobCategory: [null, [Validators.required]],
      jobType: [null, [Validators.required]],
      state: [null, [Validators.required]],
      publishedStatus: ['published', [Validators.required]],
      totalVacancy: [null],
      salary: [null],
      qualification: [[]],
      ageLimitMin: [null],
      ageLimitMax: [null],
      applyStartDate: [null],
      applyEndDate: [null],
      feeGeneral: [null],
      feeObc: [null],
      feeSc: [null],
      feeSt: [null],
      feeFemale: [null],
      feePh: [null],
      feeEws: [null],
      officialNotificationUrl: [null],
      applyLink: [null],
      description: [null],
      image: [null],
      status: [true],
      examStages: this.fb.array([]),
      importantLinks: this.fb.array([]),
      importantDates: this.fb.array([]),
    });
  }

  // ngAfterViewInit(): void {

  // }

  ngOnInit(): void {
    // setTimeout(
    //   () => ((this.attrEditorReady = true), this.patchValueInEditor()),
    // );
    this.editorOptions = this.ckEditorService.editorOptions;

    this.examStagesArray.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.collectStageAttributeKeys();
      this.collectSubjectAttributeKeys();
    });

    this.searchCategory.pipe(debounceTime(1000)).subscribe((val) => {
      this.categorySearch = val;
      this.listOfCategories = [];
      this.categoryPageIndex = 1;
      this.getAllCategories();
    });

    this.getAllCategories();
    this.getAllStates();

    if (this.idForUpdate) {
      this.getJobAlertById();
    } else {
      setTimeout(
        () => ((this.attrEditorReady = true), this.patchValueInEditor()),
      );
      this.addImportantDate('Exam Date');
      this.addImportantDate('Admit Card Date');
    }
  }

  /* ──────────────────── States ──────────────────── */

  getAllStates(): void {
    this.statesLoading = true;
    this.httpRequestService.request('get', 'generals/state').subscribe(
      (result: any) => {
        this.listOfStates = [{ name: this.ALL_INDIA }, ...result.data];
        this.statesLoading = false;
      },
      () => {
        this.statesLoading = false;
      },
    );
  }

  /* ──────────────────── Important Dates FormArray ──────────────────── */

  get importantDatesArray(): FormArray {
    return this.addUpdateJobAlertForm.get('importantDates') as FormArray;
  }

  buildImportantDateGroup(data?: any): FormGroup {
    return this.fb.group({
      title: [data?.title ?? null],
      value: [data?.value ?? null],
    });
  }

  addImportantDate(defaultTitle: string = ''): void {
    this.importantDatesArray.push(
      this.buildImportantDateGroup({ title: defaultTitle }),
    );
  }

  removeImportantDate(index: number): void {
    this.importantDatesArray.removeAt(index);
  }

  /* ──────────────────── Important Links FormArray ──────────────────── */

  get importantLinksArray(): FormArray {
    return this.addUpdateJobAlertForm.get('importantLinks') as FormArray;
  }

  buildImportantLinkGroup(data?: any): FormGroup {
    return this.fb.group({
      title: [data?.title ?? null],
      value: [data?.value ?? null],
    });
  }

  addImportantLink(): void {
    this.importantLinksArray.push(this.buildImportantLinkGroup());
  }

  removeImportantLink(index: number): void {
    this.importantLinksArray.removeAt(index);
  }

  /* ──────────────────── Exam Stages FormArray ──────────────────── */

  get examStagesArray(): FormArray {
    return this.addUpdateJobAlertForm.get('examStages') as FormArray;
  }

  buildStageGroup(data?: any): FormGroup {
    return this.fb.group({
      stageOrder: [data?.stageOrder ?? null, [Validators.required]],
      stageName: [data?.stageName ?? null, [Validators.required]],
      examDate: [data?.examDate ? new Date(data.examDate) : null],
      totalMarks: [data?.totalMarks ?? null],
      totalQuestions: [data?.totalQuestions ?? null],
      totalDurations: [data?.totalDurations ?? null],
      instructions: [
        Array.isArray(data?.instructions)
          ? data.instructions.join('<br>')
          : (data?.instructions ?? ''),
      ],
      attributes: this.fb.array(
        this.attributesToArray(data?.attributes).map((a) =>
          this.buildAttributeGroup(a),
        ),
      ),
      subjects: this.fb.array(
        (data?.subjects ?? []).map((s: any) => this.buildSubjectGroup(s)),
      ),
    });
  }

  buildSubjectGroup(data?: any): FormGroup {
    return this.fb.group({
      name: [data?.name ?? null],
      questions: [data?.questions ?? null],
      marks: [data?.marks ?? null],
      attributes: this.fb.array(
        this.attributesToArray(data?.attributes).map((a) =>
          this.buildAttributeGroup(a),
        ),
      ),
    });
  }

  addStage(): void {
    this.examStagesArray.push(this.buildStageGroup());
  }

  removeStage(index: number): void {
    this.examStagesArray.removeAt(index);
  }

  getSubjectsArray(stageIndex: number): FormArray {
    return this.examStagesArray.at(stageIndex).get('subjects') as FormArray;
  }

  addSubject(stageIndex: number): void {
    this.getSubjectsArray(stageIndex).push(this.buildSubjectGroup());
  }

  removeSubject(stageIndex: number, subjectIndex: number): void {
    this.getSubjectsArray(stageIndex).removeAt(subjectIndex);
  }

  /* ──────────────────── Attributes (Map) helpers ──────────────────── */

  buildAttributeGroup(data?: any): FormGroup {
    return this.fb.group({
      type: [data?.type ?? 'text'],
      key: [data?.key ?? null],
      value: [data?.value ?? null],
    });
  }

  /** API returns attributes as array of {key, type, value}; fallback handles old Map format */
  attributesToArray(attrs: any): { key: string; type: string; value: any }[] {
    if (!attrs) return [];
    if (Array.isArray(attrs)) {
      return attrs.map((a: any) => ({
        key: a.key ?? '',
        type: a.type ?? 'text',
        value: a.value,
      }));
    }
    // backwards compat: old Mongoose Map (plain object)
    return Object.keys(attrs).map((k) => ({
      key: k,
      type: 'text',
      value: attrs[k],
    }));
  }

  /* stage-level attributes */
  getStageAttributesArray(stageIndex: number): FormArray {
    return this.examStagesArray.at(stageIndex).get('attributes') as FormArray;
  }
  addStageAttribute(stageIndex: number): void {
    this.getStageAttributesArray(stageIndex).push(this.buildAttributeGroup());
  }
  removeStageAttribute(stageIndex: number, attrIndex: number): void {
    this.getStageAttributesArray(stageIndex).removeAt(attrIndex);
  }

  /* subject-level attributes */
  getSubjectAttributesArray(
    stageIndex: number,
    subjectIndex: number,
  ): FormArray {
    return this.getSubjectsArray(stageIndex)
      .at(subjectIndex)
      .get('attributes') as FormArray;
  }
  addSubjectAttribute(stageIndex: number, subjectIndex: number): void {
    this.getSubjectAttributesArray(stageIndex, subjectIndex).push(
      this.buildAttributeGroup(),
    );
  }
  removeSubjectAttribute(
    stageIndex: number,
    subjectIndex: number,
    attrIndex: number,
  ): void {
    this.getSubjectAttributesArray(stageIndex, subjectIndex).removeAt(
      attrIndex,
    );
  }

  /* ──────────────────── Category dropdown ──────────────────── */

  onExamCategorySearch(event: string): void {
    this.searchCategory.next(event);
  }

  loadMoreCategories(): void {
    if (this.totalCategoryCount !== this.listOfCategories.length) {
      this.categoryPageIndex++;
      this.getAllCategories(
        this.categoryPageSize * (this.categoryPageIndex - 1),
      );
    }
  }

  getAllCategories(skip = 0): void {
    const params: any = { skip, limit: this.categoryPageSize };
    if (this.categorySearch) params.search = this.categorySearch;
    this.httpRequestService.request('get', 'exam-categories', params).subscribe(
      (result: any) => {
        this.listOfCategories.push(...result.data);
        this.totalCategoryCount = result.totalCount;
      },
      () => {},
    );
  }

  /* ──────────────────── Sun Editor ──────────────────── */

  onDescriptionChange(content: any): void {
    this.addUpdateJobAlertForm.patchValue(
      { description: content.content },
      { emitEvent: false },
    );
  }

  onInstructionsChange(content: any, stageIndex: number): void {
    const stageGroup = this.examStagesArray.at(stageIndex) as FormGroup;
    stageGroup.patchValue(
      { instructions: content.content },
      { emitEvent: false },
    );
  }

  onStageAttrValueChange(
    content: any,
    stageIndex: number,
    attrIndex: number,
  ): void {
    (
      this.getStageAttributesArray(stageIndex).at(attrIndex) as FormGroup
    ).patchValue({ value: content.content }, { emitEvent: false });
  }

  onSubjectAttrValueChange(
    content: any,
    stageIndex: number,
    subjectIndex: number,
    attrIndex: number,
  ): void {
    (
      this.getSubjectAttributesArray(stageIndex, subjectIndex).at(
        attrIndex,
      ) as FormGroup
    ).patchValue({ value: content.content }, { emitEvent: false });
  }

  onStageAttrTypeChange(
    newType: string,
    stageIndex: number,
    attrIndex: number,
  ): void {
    if (newType !== 'textarea') return;
    const currentValue =
      (
        this.getStageAttributesArray(stageIndex).at(attrIndex) as FormGroup
      ).get('value')?.value ?? '';
    setTimeout(() => {
      const editor = this.editors.find(
        (e) => e.editorID === `stage-attr-${stageIndex}-${attrIndex}`,
      );
      if (editor) editor.setContents(currentValue);
    });
  }

  onSubjectAttrTypeChange(
    newType: string,
    stageIndex: number,
    subjectIndex: number,
    attrIndex: number,
  ): void {
    if (newType !== 'textarea') return;
    const currentValue =
      (
        this.getSubjectAttributesArray(stageIndex, subjectIndex).at(
          attrIndex,
        ) as FormGroup
      ).get('value')?.value ?? '';
    setTimeout(() => {
      const editor = this.editors.find(
        (e) =>
          e.editorID ===
          `subject-attr-${stageIndex}-${subjectIndex}-${attrIndex}`,
      );
      if (editor) editor.setContents(currentValue);
    });
  }

  /* ──────────────────── Load for update ──────────────────── */

  getJobAlertById(): void {
    this.httpRequestService
      .request('get', `job-alerts/${this.idForUpdate}`)
      .subscribe(
        (result: any) => {
          const d = result.data;

          this.examStagesArray.clear();
          (d.examStages ?? []).forEach((stage: any) => {
            this.examStagesArray.push(this.buildStageGroup(stage));
          });

          this.importantLinksArray.clear();
          (d.importantLinks ?? []).forEach((link: any) => {
            this.importantLinksArray.push(this.buildImportantLinkGroup(link));
          });

          this.importantDatesArray.clear();
          (d.importantDates ?? []).forEach((dateItem: any) => {
            this.importantDatesArray.push(
              this.buildImportantDateGroup(dateItem),
            );
          });

          if (!d.importantDates || d.importantDates.length === 0) {
            if (d.examDate) {
              this.importantDatesArray.push(
                this.buildImportantDateGroup({
                  title: 'Exam Date',
                  value: d.examDate,
                }),
              );
            } else {
              this.addImportantDate('Exam Date');
            }
            if (d.admitCard) {
              this.importantDatesArray.push(
                this.buildImportantDateGroup({
                  title: 'Admit Card Date',
                  value: d.admitCard,
                }),
              );
            } else {
              this.addImportantDate('Admit Card Date');
            }
          }

          this.addUpdateJobAlertForm.patchValue({
            title: d.title,
            jobCategory: d.jobCategory?._id ?? d.jobCategory,
            jobType: d.jobType,
            state: d.state,
            publishedStatus: d.publishedStatus ?? 'unpublished',
            totalVacancy: d.totalVacancy,
            salary: d.salary,
            qualification: d.qualification ?? [],
            ageLimitMin: d.ageLimit?.min,
            ageLimitMax: d.ageLimit?.max,
            applyStartDate: d.applyStartDate
              ? new Date(d.applyStartDate)
              : null,
            applyEndDate: d.applyEndDate ? new Date(d.applyEndDate) : null,
            feeGeneral: d.applicationFee?.general,
            feeObc: d.applicationFee?.obc,
            feeSc: d.applicationFee?.sc,
            feeSt: d.applicationFee?.st,
            feeFemale: d.applicationFee?.female,
            feePh: d.applicationFee?.ph,
            feeEws: d.applicationFee?.ews,
            officialNotificationUrl: d.officialNotificationUrl,
            applyLink: d.applyLink,
            image: d.image?._id ?? d.image ?? null,
            status: d.status,
          });

          this.imageMeta = d.image?._id
            ? { _id: d.image._id, path: d.image.path }
            : null;

          setTimeout(() => {
            console.log('Available editors:', this.editors.length);
            console.log(
              'Editor IDs:',
              this.editors.map((e) => e.editorID),
            );

            this.editors.forEach((editor: any) => {
              console.log('Processing editor:', editor.editorID);

              if (editor.editorID === 'description') {
                console.log('Setting description:', d.description);
                editor.setContents(d.description ?? '');
              }
              if (editor.editorID?.startsWith('instructions-')) {
                const idx = parseInt(editor.editorID.split('-')[1], 10);
                const stageData = d.examStages?.[idx];
                console.log(
                  `Setting instructions for stage ${idx}:`,
                  stageData?.instructions,
                );
                editor.setContents(stageData?.instructions ?? '');
              }
              console.log(editor.editorID);
            });
            this.attrEditorReady = true;
            this.patchValueInEditor(d);
          }, 0); // Changed to 0ms
        },
        () => {},
      );
  }
  patchValueInEditor(d?: any) {
    setTimeout(() => {
      console.log('Available editors:', this.editors.length);
      console.log(
        'Editor IDs:',
        this.editors.map((e) => e.editorID),
      );

      this.editors.forEach((editor: any) => {
        if (editor.editorID?.startsWith('stage-attr-')) {
          const parts = editor.editorID.split('-');
          const si = parseInt(parts[2], 10);
          const ai = parseInt(parts[3], 10);
          const stageData = d.examStages?.[si];
          const attrValue = stageData?.attributes?.[ai]?.value ?? '';
          console.log(`Setting stage attr ${si}-${ai}:`, attrValue);
          editor.setContents(attrValue);
        }
        if (editor.editorID?.startsWith('subject-attr-')) {
          const parts = editor.editorID.split('-');
          const si = parseInt(parts[2], 10);
          const ji = parseInt(parts[3], 10);
          const ai = parseInt(parts[4], 10);
          const subjectData = d.examStages?.[si]?.subjects?.[ji];
          const attrValue = subjectData?.attributes?.[ai]?.value ?? '';
          console.log(`Setting subject attr ${si}-${ji}-${ai}:`, attrValue);
          editor.setContents(attrValue);
        }
      });
    });
  }
  /* ──────────────────── Submit ──────────────────── */

  submit(): void {
    // Remove blank exam stages and blank subjects
    for (let i = this.examStagesArray.length - 1; i >= 0; i--) {
      // Remove blank subjects first
      const subjectsArray = this.getSubjectsArray(i);
      for (let j = subjectsArray.length - 1; j >= 0; j--) {
        const subject = subjectsArray.at(j).value;
        const isBlankSubject =
          (!subject.name || subject.name.trim() === '') &&
          !subject.questions &&
          !subject.marks &&
          (!subject.attributes ||
            subject.attributes.every((a: any) => !a.key && !a.value));
        if (isBlankSubject) {
          subjectsArray.removeAt(j);
        }
      }

      const stage = this.examStagesArray.at(i).value;
      const isBlank =
        !stage.stageOrder &&
        (!stage.stageName || stage.stageName.trim() === '') &&
        !stage.examDate &&
        !stage.totalMarks &&
        (!stage.subjects || stage.subjects.length === 0) &&
        (!stage.instructions || stage.instructions.length === 0) &&
        (!stage.attributes ||
          stage.attributes.every((a: any) => !a.key && !a.value));
      if (isBlank) {
        this.examStagesArray.removeAt(i);
      }
    }

    if (!this.addUpdateJobAlertForm.valid) {
      this.markFormGroupTouched(this.addUpdateJobAlertForm);
      return;
    }
    if (this.idForUpdate) {
      this.saveJobAlert(
        'put',
        `job-alerts/${this.idForUpdate}`,
        'Job Alert Updated Successfully',
      );
    } else {
      this.saveJobAlert('post', 'job-alerts', 'Job Alert Added Successfully');
    }
  }

  saveJobAlert(method: string, url: string, successMsg: string): void {
    this.buttonLoading = true;
    const raw = this.addUpdateJobAlertForm.value;

    const finalData = {
      title: raw.title,
      jobCategory: raw.jobCategory,
      jobType: raw.jobType,
      state: raw.state,
      totalVacancy: raw.totalVacancy,
      salary: raw.salary,
      qualification: raw.qualification,
      ageLimit: { min: raw.ageLimitMin, max: raw.ageLimitMax },
      applyStartDate: raw.applyStartDate,
      applyEndDate: raw.applyEndDate,
      applicationFee: {
        general: raw.feeGeneral,
        obc: raw.feeObc,
        sc: raw.feeSc,
        st: raw.feeSt,
        female: raw.feeFemale,
        ph: raw.feePh,
        ews: raw.feeEws,
      },
      publishedStatus: raw.publishedStatus,
      officialNotificationUrl: raw.officialNotificationUrl,
      applyLink: raw.applyLink,
      importantLinks: raw.importantLinks,
      importantDates: raw.importantDates,
      description: raw.description,
      // ✅ raw.image always holds _id (either from fresh upload or loaded from DB)
      image: raw.image ?? null,
      examStages: (raw.examStages || []).map((stage: any) => {
        const s: any = { ...stage };

        // instructions -> rich text HTML string
        const instrHtml = (s.instructions ?? '').toString().trim();
        if (instrHtml) {
          s.instructions = instrHtml;
        } else {
          delete s.instructions;
        }

        // stage attributes rows -> array, drop blank keys
        s.attributes = (s.attributes ?? [])
          .filter((a: any) => (a?.key ?? '').toString().trim())
          .map((a: any) => ({
            type: a.type ?? 'text',
            key: (a.key ?? '').toString().trim(),
            value: a.value,
          }));
        if (s.attributes.length === 0) delete s.attributes;

        // subjects + their attributes
        s.subjects = (s.subjects ?? []).map((sub: any) => {
          const subj: any = { ...sub };
          subj.attributes = (subj.attributes ?? [])
            .filter((a: any) => (a?.key ?? '').toString().trim())
            .map((a: any) => ({
              type: a.type ?? 'text',
              key: (a.key ?? '').toString().trim(),
              value: a.value,
            }));
          if (subj.attributes.length === 0) delete subj.attributes;
          return subj;
        });
        if (s.subjects.length === 0) delete s.subjects;

        return s;
      }),
      status: raw.status,
    };

    this.httpRequestService.request(method, url, finalData).subscribe(
      () => {
        this.notificationService.success('', successMsg);

        let targetIndex = this.returnIndex;
        const userRole = this.localStorageService.getItem('user')?.role;
        // If subAdmin creates a new job alert, it stays unapproved, so direct them to unapproved tab
        if (!this.idForUpdate && userRole === 'subAdmin') {
          targetIndex = 1;
        }

        this.router.navigate(['/main/job-alerts'], {
          queryParams: { index: targetIndex },
        });
        this.buttonLoading = false;
      },
      (error: any) => {
        if (error.error?.errors) {
          const allErrors: string[] = Object.values(error.error.errors);
          allErrors.forEach((err) => this.notificationService.error('', err));
        } else {
          this.notificationService.error(
            '',
            error.error?.message ?? 'Something went wrong',
          );
        }
        this.buttonLoading = false;
      },
    );
  }

  /* ──────────────────── Image Upload ──────────────────── */

  customRequestHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  uploadImageFile(
    info: { file: NzUploadFile },
    controlName: 'image' | 'thumbnailImage',
  ): void {
    const isThumb = controlName === 'thumbnailImage';
    switch (info.file.status) {
      case 'uploading':
        isThumb ? (this.thumbImageLoading = true) : (this.imageLoading = true);
        break;
      case 'done':
        const uploadedId = info.file.response?.data?._id;
        const uploadedPath = info.file.response?.data?.path;
        // ✅ Store _id in form control
        this.addUpdateJobAlertForm.patchValue({ [controlName]: uploadedId });
        // ✅ Update meta with new upload's _id and path for preview
        if (controlName === 'image') {
          this.imageMeta = uploadedId
            ? { _id: uploadedId, path: uploadedPath }
            : null;
        }
        if (controlName === 'thumbnailImage') {
          this.thumbMeta = uploadedId
            ? { _id: uploadedId, path: uploadedPath }
            : null;
        }
        isThumb
          ? (this.thumbImageLoading = false)
          : (this.imageLoading = false);
        break;
      case 'error':
        this.msg.error('Network error while uploading image');
        isThumb
          ? (this.thumbImageLoading = false)
          : (this.imageLoading = false);
        break;
    }
  }

  beforeMediaUpload = (file: NzUploadFile): Observable<any> => {
    return new Observable((observer: Observer<boolean>) => {
      const allowed = [
        'image/jpg',
        'image/jpeg',
        'image/png',
        'image/JPG',
        'image/JPEG',
        'image/PNG',
      ];
      if (!allowed.includes(file.type ?? '')) {
        this.msg.error('Only JPG / PNG images are allowed!');
        observer.complete();
        return;
      }
      const isLt3M = (file.size ?? 0) / 1024 / 1024 < 3;
      if (!isLt3M) {
        this.msg.error('Image must be smaller than 3 MB!');
        observer.complete();
        return;
      }
      observer.next(true);
      observer.complete();
    });
  };

  removeImage(controlName: 'image' | 'thumbnailImage'): void {
    const path =
      controlName === 'image' ? this.imageMeta?.path : this.thumbMeta?.path;

    this.addUpdateJobAlertForm.patchValue({ [controlName]: null });

    if (controlName === 'image') this.imageMeta = null;
    if (controlName === 'thumbnailImage') this.thumbMeta = null;

    if (!path) return; // nothing to delete on server

    this.httpRequestService
      .request('post', 'media/delete-media', { path })
      .subscribe(
        () =>
          this.notificationService.success('', 'Image Deleted Successfully'),
        () => {},
      );
  }

  /* ──────────────────── Helpers ──────────────────── */

  private collectStageAttributeKeys(): void {
    const keys = new Set<string>();
    this.examStagesArray.controls.forEach((stageCtrl) => {
      const attrs = (stageCtrl.get('attributes') as FormArray)?.controls ?? [];
      attrs.forEach((attrCtrl) => {
        const key = attrCtrl.get('key')?.value?.toString().trim();
        if (key) keys.add(key);
      });
    });
    this.suggestedStageAttributes = Array.from(keys);
  }

  private collectSubjectAttributeKeys(): void {
    const keys = new Set<string>();
    this.examStagesArray.controls.forEach((stageCtrl) => {
      const subjects = (stageCtrl.get('subjects') as FormArray)?.controls ?? [];
      subjects.forEach((subCtrl) => {
        const attrs = (subCtrl.get('attributes') as FormArray)?.controls ?? [];
        attrs.forEach((attrCtrl) => {
          const key = attrCtrl.get('key')?.value?.toString().trim();
          if (key) keys.add(key);
        });
      });
    });
    this.suggestedSubjectAttributes = Array.from(keys);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((ctrl: AbstractControl) => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
      if (ctrl instanceof FormGroup) this.markFormGroupTouched(ctrl);
      if (ctrl instanceof FormArray) {
        ctrl.controls.forEach((c) => {
          if (c instanceof FormGroup) this.markFormGroupTouched(c);
        });
      }
    });
  }
}
